import {guard, restore, sample} from 'effector';
import i18next from 'i18next';

import {toast} from 'react-toastify';

import {setXmlFile} from 'components/pages/DocumentsCreate/InvoiceNew/helpers/buildXml';
import {VAT_CATEGORY} from 'components/pages/DocumentsCreate/InvoiceNew/helpers/consts';
import {RestError, showRestError} from 'core/errors';

import {InvoiceLine, TaxesAndTotalAllowanceCharge} from 'types/invoices';

import {
    $invoiceIsRemoved,
    $invoiceState,
    CreateInvoiceGate,
    deleteInvoiceFx,
    updateInvoiceStatusFx,
} from '../common';

import {calcUpdatingTaxesAndTotalAllowanceCharge} from './helpers/calcChargeData';

import {errorHandlerNoFirs} from './helpers/errorHandlerNoFirs';

import {
    $pageValues,
    updatePageValues,
    $invoiceCreationPage,
    invoiceCreationPage,
    goNextPage,
    goPrevPage,
    arrStores,
    $infoValues,
    updateInfoValues,
    $invoiceLineValues,
    removeInvoiceLineValues,
    updateTaxesAndTotal,
    $taxesAndTotal,
    updateIRN,
    $invoiceLineChargeData,
    setInvoiceLineChargeData,
    fetchXmlValidationFx,
    fetchInvocieXmlFx,
    fetchInvoiceIrnFx,
    guarderFetchInvoice,
    updateCustomerCompanyNameOrTIN,
    $validationErrorToPassNext,
    handleValidationErrorToPassNext,
    updateTaxesAndTotalFromInvoiceLines,
    updateInfoValuesCityAndZip,
    updateTaxesAndTotalAllowanceCharge,
    removeTaxesAndTotalAllowanceCharge,
    updateTaxesAndTotalRoundAndPrepayd,
    setTaxesAndTotalFromXml,
    updateInfoValuesPaymentMean,
    $invoiceCreatingPending,
    setInvoiceCreatingPending,
    $invoiceLinesHolder,
    setInvoiceLinesHolder,
    updateInvoiceLinesHolder,
    removeInvoiceLinesHolder,
    copyInvoiceLineData,
    $initalLineValuesHolder,
    setInitalLineValuesHolder,
    addInvoiceLineValues,
    updateAllDataFromInvoiceLinesHolder,
    $showGroupLinesByGtinModal,
    $forceAllowToSend,
    setForceAllowToSend,
} from './index';

$validationErrorToPassNext
    .on(handleValidationErrorToPassNext, (_, data) => data)
    .reset(CreateInvoiceGate.close);

interface ExtendedError extends Error {
    fullErrorsList?: {code: string; message: string}[];
}
fetchXmlValidationFx.fail.watch((error) => {
    const getError = error.error as ExtendedError;

    const hasFatalError = getError.fullErrorsList?.find((i) => i.code === '0400');
    if (hasFatalError) {
        return showRestError(error.error as RestError);
    }

    handleValidationErrorToPassNext(true);
});

fetchInvocieXmlFx.fail.watch(({error}) => showRestError(error as RestError));

fetchInvocieXmlFx.done.watch((data) => {
    const {reportId} = data.result.data.data;
    toast.success(i18next.t('pages.documents.form.documentCreated', {reportId}), {
        className: 'Toastify__toast__wide',
    });

    setInvoiceCreatingPending(true);

    setTimeout(() => {
        updateInvoiceStatusFx({
            invoiceId: reportId,
            status: 'PENDING',
        });
    }, 1000);
});

fetchInvoiceIrnFx.fail.watch(({error}) => {
    errorHandlerNoFirs(error);
    return showRestError(error as RestError);
});

$invoiceIsRemoved.on(deleteInvoiceFx.doneData, () => true);

$invoiceCreationPage.on(invoiceCreationPage, (_, data) => data);
$invoiceCreationPage.on(goNextPage, (store) => store + 1);
$invoiceCreationPage.on(goPrevPage, (store) => store - 1);

$pageValues.on(updatePageValues, (_, data) => data);

$infoValues.on(updateInfoValues, (store, data) => {
    const {
        customerZip: oldCustomerZip,
        customerCity: oldCustomerCity,
        customerCountry: oldCustomerCountry,
        customerStreet: oldCustomerStreet,
    } = store;
    const {
        customerZip: newCustomerZip,
        customerCity: newCustomerCity,
        customerCountry: newCustomerCountry,
        customerStreet: newCustomerStreet,
    } = data;

    return {
        ...data,
        customerZip: newCustomerZip ?? oldCustomerZip,
        customerCity: newCustomerCity ?? oldCustomerCity,
        customerCountry: newCustomerCountry ?? oldCustomerCountry,
        customerStreet: newCustomerStreet ?? oldCustomerStreet,
    };
});
$infoValues.on(updateInfoValuesCityAndZip, (store, data) => ({
    ...store,
    ...data,
}));
$infoValues.on(updateIRN, (store, data) => ({...store, irn: data}));
$infoValues.on(updateCustomerCompanyNameOrTIN, (store, data) => ({
    ...store,
    customerCompanyNameOrTIN: data,
}));

export const $infoValuesPaymentMean = restore(updateInfoValuesPaymentMean, false).reset(
    CreateInvoiceGate.close,
);

const REQUIRED_FIELDS: (keyof InvoiceLine)[] = [
    'qty',
    'price',
    'lines',
    'vatCategory',
    'vat',
    'lineAmountWVat',
];

const isFieldFilled = (line: InvoiceLine | undefined, field: keyof InvoiceLine): boolean => {
    if (!line) {
        return false;
    }

    if (!(field in line) || line[field] === undefined || line[field] === null) {
        return false;
    }

    const fieldValue = line[field];

    if (field === 'vatCategory' || field === 'serviceCategory') {
        const objValue = fieldValue as {value?: string} | undefined;
        return Boolean(objValue?.value && objValue.value !== '');
    }

    const stringValue = String(fieldValue);
    return stringValue !== '';
};

const areRequiredFieldsFilled = (line: InvoiceLine | undefined): boolean => {
    if (!line) {
        return false;
    }

    return REQUIRED_FIELDS.every((field) => isFieldFilled(line, field));
};

const hasChargesData = (line: InvoiceLine | undefined): boolean => {
    if (!line?.charges) {
        return false;
    }
    const {charges} = line;
    return Boolean(
        charges.amountXaf ||
            charges.reason ||
            charges.ratePercent !== undefined ||
            charges.discountRateAndBaseAmount !== undefined,
    );
};

const shouldDisablePasteForMarkedItem = (line: InvoiceLine | undefined): boolean => {
    if (!line) {
        return false;
    }

    const hasPrice = isFieldFilled(line, 'price');
    const hasVatCategory = isFieldFilled(line, 'vatCategory');
    const hasCharges = hasChargesData(line);

    return hasPrice || hasVatCategory || hasCharges;
};

const calculateCopyActions = (line: InvoiceLine | undefined) => {
    const isRequiredFieldsFilled = areRequiredFieldsFilled(line);
    const isMarkedItem = line?.isMarkedItem ?? false;

    const allowToPaste = isMarkedItem ? !shouldDisablePasteForMarkedItem(line) : false;

    return {
        allowToCopy: isMarkedItem && isRequiredFieldsFilled,
        allowToDuplicateDown: !isMarkedItem && isRequiredFieldsFilled,
        allowToPaste,
    };
};

const updateLineWithCopyActions = (line: InvoiceLine): InvoiceLine => ({
    ...line,
    copyActions: calculateCopyActions(line),
});

$invoiceLinesHolder.on(updateAllDataFromInvoiceLinesHolder, (store, data) => data);

$invoiceLinesHolder.on(setInvoiceLinesHolder, (store, data) => {
    const dataWithCalculatedActions = data.map((line) => updateLineWithCopyActions(line));
    return [...dataWithCalculatedActions, ...store];
});
$invoiceLinesHolder.on(removeInvoiceLinesHolder, (store, index) =>
    store.filter((_, indx) => index !== indx),
);
$invoiceLinesHolder.on(updateInvoiceLinesHolder, (store, data) => {
    const {index, data: innerData} = data;
    const storeHolder = [...store];

    if (Array.isArray(innerData)) {
        innerData.forEach((item) => {
            const {columnId, value} = item;
            if (value === null) {
                delete storeHolder[index]?.charges;
            } else {
                storeHolder[index] = {
                    ...storeHolder[index],
                    [columnId]: value,
                };
            }
        });

        if (storeHolder[index]) {
            storeHolder[index] = updateLineWithCopyActions(storeHolder[index]);
        }

        return storeHolder;
    }

    const {columnId, value} = innerData as {columnId: string; value: string | null};

    if (value === null) {
        delete storeHolder[index]?.charges;
        if (storeHolder[index]) {
            storeHolder[index] = updateLineWithCopyActions(storeHolder[index]);
        }
        return storeHolder;
    }

    storeHolder[index] = {
        ...storeHolder[index],
        [columnId]: value,
    };
    storeHolder[index] = updateLineWithCopyActions(storeHolder[index]);

    return storeHolder;
});

const calculateLines = (line: InvoiceLine): string => {
    const {qty, price, charges} = line;

    if (!qty || !price || isNaN(Number(qty)) || isNaN(Number(price))) {
        return line.lines || '';
    }

    const baseValue = Number(price) * Number(qty);

    if (charges?.amountXaf) {
        const multiplier = charges.isCharge === 'true' ? 1 : -1;
        const xaf = Number(charges.amountXaf) * multiplier;
        return (baseValue + xaf).toString();
    }

    return baseValue.toFixed(2);
};

const calculateLineAmountWVat = (line: InvoiceLine): string => {
    const {lines, vat, vatCategory} = line;

    if (!vatCategory?.value || vat === '0' || !vat) {
        const linesValue = Number(lines);
        return isNaN(linesValue) ? line.lineAmountWVat || '' : linesValue.toFixed(2);
    }

    if (!lines || isNaN(Number(lines)) || isNaN(Number(vat))) {
        return line.lineAmountWVat || '';
    }

    const linesValue = Number(lines);
    const vatValue = Number(vat);
    const result = linesValue + (linesValue * vatValue) / 100;

    return result.toFixed(2);
};

$invoiceLinesHolder.on(copyInvoiceLineData, (store, data) => {
    const {index: sourceIndex, initialLineData} = data;
    const sourceLine =
        sourceIndex === -1 && initialLineData
            ? (initialLineData as InvoiceLine)
            : store[sourceIndex];
    if (!sourceLine) {
        return store;
    }

    const dataToCopy = {
        price: sourceLine.price,
        charges:
            sourceIndex === -1 && initialLineData
                ? $invoiceLineChargeData.getState() ?? undefined
                : sourceLine.charges,
        vatCategory: sourceLine.vatCategory,
        vat: sourceLine.vat,
    };

    const updatedStore = [...store];

    updatedStore.forEach((line, index) => {
        if (index <= sourceIndex) {
            return;
        }

        const isMarkedItem = line.isMarkedItem === true;
        const allowToPaste = line.copyActions?.allowToPaste === true;

        if (isMarkedItem && allowToPaste) {
            const updatedLine: InvoiceLine = {
                ...line,
                ...dataToCopy,
            };

            updatedLine.lines = calculateLines(updatedLine);

            updatedLine.lineAmountWVat = calculateLineAmountWVat(updatedLine);

            updatedStore[index] = updatedLine;

            updatedStore[index] = updateLineWithCopyActions(updatedStore[index]);
        }
    });

    return updatedStore;
});

// $invoiceLineValues.on(addInvoiceLineValues, (store, data) => {
//     const invoiceLineChargeData = $invoiceLineChargeData.getState();
//     const mappedData = data.map((i, index) => {
//         const invoiceLineChargeDataItem = invoiceLineChargeData[index];
//         if (!invoiceLineChargeDataItem) {
//             return i;
//         }

//         return {
//             ...i,
//             charges: invoiceLineChargeDataItem,
//         };
//     });

//     return [...mappedData, ...store];
// });
$invoiceLineValues.on(addInvoiceLineValues, (_, data) => data);

$invoiceLineValues.on(removeInvoiceLineValues, (store, index) =>
    store.filter((j, indx) => index !== indx),
);

$initalLineValuesHolder.on(setInitalLineValuesHolder, (store, data) => ({
    ...store,
    ...data,
}));

function calculateTaxValues(val: InvoiceLine[]) {
    return val.reduce(
        (total, i) => {
            const totalMainKey = `${i.vatCategory.value as keyof typeof total} :: ${Number(i.vat)}`;

            if (totalMainKey in total) {
                const newTaxable1 = total[totalMainKey]['taxableAmount'] + Number(i.lines);
                const newTax =
                    total[totalMainKey]['taxAmount'] +
                    Number(i.lines) * (total[totalMainKey]['rate'] / 100);
                return {
                    ...total,
                    [totalMainKey]: {
                        rate: total[totalMainKey]['rate'],
                        taxableAmount: newTaxable1,
                        taxAmount: newTax,
                    },
                };
            }
            return {
                ...total,
                [totalMainKey]: {
                    taxableAmount: Number(i.lines),
                    rate: Number(i.vat),
                    taxAmount: Number(i.lines) * (Number(i.vat) / 100),
                },
            };
        },
        {} as Record<
            string,
            {
                taxableAmount: number;
                rate: number;
                taxAmount: number;
            }
        >,
    );
}

guard({
    clock: $invoiceLineValues,
    filter: () => $forceAllowToSend.getState(),
    target: guarderFetchInvoice,
});

const goToTaxesUpdate = guard({
    clock: $invoiceLineValues,
    filter: () => !$forceAllowToSend.getState(),
});

sample({
    clock: goToTaxesUpdate,
    fn: (val) => {
        const taxValues = calculateTaxValues(val);
        const taxCategoryDisplay = (key: string) =>
            VAT_CATEGORY.find((i) => i.value === key.split(' :: ')[0]);

        const valuesToTaxesBlock = Object.entries(taxValues).map(([key, value]) => ({
            taxCategory: key,
            taxableAmount: value.taxableAmount.toFixed(2),
            rate: value.rate.toFixed(1),
            taxAmount: value.taxAmount.toFixed(2),

            taxCategoryDisplay: taxCategoryDisplay(key)?.label,
            taxableAmountDisplay: value.taxableAmount.toFixed(2),
            rateDisplay: value.rate.toFixed(1),
            taxAmountDisplay: value.taxAmount.toFixed(2),
        }));
        const setTaxTotal = valuesToTaxesBlock.reduce((t, i) => t + Number(i.taxAmount), 0);
        const LEA = valuesToTaxesBlock.reduce((t, i) => t + Number(i.taxableAmount), 0);
        const taxes = $taxesAndTotal.getState();
        // const taxesAndTotalAllowanceCharge = $taxesAndTotalAllowanceCharge.getState()
        const calcTaxExclusiveAmount =
            LEA - Number(taxes.data.allowanceTotalAmount) + Number(taxes.data.chargeTotalAmount);
        const calcTaxInclusiveAmount = setTaxTotal + calcTaxExclusiveAmount;
        const calcPayableAmount =
            calcTaxInclusiveAmount -
            (taxes.data.prepaidAmount ? Number(taxes.data.prepaidAmount) : 0) +
            (taxes.data.payableRoundingAmount ? Number(taxes.data.payableRoundingAmount) : 0);
        return {
            data: {
                ...taxes.data,
                calculationsByTaxCategory: valuesToTaxesBlock,
                taxTotal: setTaxTotal.toFixed(2),
                lineExtensionAmount: LEA.toFixed(2),
                taxExclusiveAmount: calcTaxExclusiveAmount.toFixed(2),
                taxExclusiveAmountDisplay: calcTaxExclusiveAmount.toFixed(2),
                taxInclusiveAmount: calcTaxInclusiveAmount.toFixed(2),
                taxInclusiveAmountDisplay: calcTaxInclusiveAmount.toFixed(2),
                whtChargeAmount_1: (LEA * (3 / 100)).toFixed(2),
                payableAmount: calcPayableAmount.toFixed(2),
                payableAmountDisplay: calcPayableAmount.toFixed(2),
            },
            allowToSend: false,
        };
    },
    target: updateTaxesAndTotalFromInvoiceLines,
});

guard({
    clock: updateTaxesAndTotalFromInvoiceLines,
    filter: () => $invoiceState.getState() === 'new',
    target: updateTaxesAndTotal,
});

$taxesAndTotal.on(updateTaxesAndTotal, (store, data) => {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const {allowanceCharge, ...rest} = data.data;
    return {
        data: {
            ...store.data,
            ...rest,
        },
        allowToSend: data.allowToSend,
    };
});

$taxesAndTotal.on(setTaxesAndTotalFromXml, (_, data) => ({
    data: data.data,
    allowToSend: false,
}));

sample({
    clock: updateTaxesAndTotalAllowanceCharge,
    fn: calcUpdatingTaxesAndTotalAllowanceCharge,
    target: $taxesAndTotal,
});

sample({
    clock: removeTaxesAndTotalAllowanceCharge,
    fn: calcUpdatingTaxesAndTotalAllowanceCharge,
    target: $taxesAndTotal,
});

sample({
    clock: updateTaxesAndTotalRoundAndPrepayd,
    fn: (item) => {
        const {name, value} = item;
        const taxesAndTotal = $taxesAndTotal.getState();
        const amounts = {
            prepaidAmount: taxesAndTotal.data.prepaidAmount,
            payableRoundingAmount: taxesAndTotal.data.payableRoundingAmount,
        };
        const amountItem = {[name]: value};
        Object.assign(amounts, amountItem);

        const p = Number(amounts.payableRoundingAmount) || 0;
        const e = Number(amounts.prepaidAmount) || 0;

        const setPayableAmountDisplay =
            Number(taxesAndTotal.data.taxInclusiveAmountDisplay) - e + p;
        Object.assign(amounts, {
            payableAmountDisplay: setPayableAmountDisplay.toFixed(2),
        });

        return {
            data: {
                ...taxesAndTotal.data,
                ...amounts,
            },
            allowToSend: false,
        };
    },
    target: updateTaxesAndTotal,
});

export const $taxesAndTotalAllowanceCharge = $taxesAndTotal.map((item) => {
    if (!item.data.allowanceCharge) {
        return {
            charges: [],
            allowances: [],
        };
    }
    return item.data.allowanceCharge.reduce(
        (t, i, index) => {
            const {isCharge, isRatePercent, ...rest} = i;
            if (i.isCharge === 'allowance') {
                return {
                    ...t,
                    allowances: [...t.allowances, {...rest, mutualIndex: index}],
                };
            }

            return {
                ...t,
                charges: [...t.charges, {...rest, mutualIndex: index}],
            };
        },
        {
            charges: [],
            allowances: [],
        } as {
            charges: TaxesAndTotalAllowanceCharge[];
            allowances: TaxesAndTotalAllowanceCharge[];
        },
    );
});

$invoiceLineChargeData.on(setInvoiceLineChargeData, (_, data) => data);

$infoValues.reset(CreateInvoiceGate.close);
$invoiceCreationPage.reset(CreateInvoiceGate.close);
$pageValues.reset(CreateInvoiceGate.close);
$invoiceLineChargeData.reset(CreateInvoiceGate.close);
$invoiceLinesHolder.reset(CreateInvoiceGate.close);
$invoiceLineValues.reset(CreateInvoiceGate.close);
$taxesAndTotal.reset(CreateInvoiceGate.close);
$initalLineValuesHolder.reset(CreateInvoiceGate.close);

guard({
    clock: $taxesAndTotal,
    filter: (i) => $invoiceState.getState() === 'new' && i.allowToSend,
    target: guarderFetchInvoice,
});

sample({
    clock: guarderFetchInvoice,
    source: arrStores,
    fn: (value) => setXmlFile(value),
    target: fetchXmlValidationFx,
});

sample({
    clock: [fetchXmlValidationFx.doneData, handleValidationErrorToPassNext],
    source: arrStores,
    fn: (value) => setXmlFile(value),
    target: fetchInvocieXmlFx,
});

$invoiceCreatingPending
    .on(setInvoiceCreatingPending, (_, data) => data)
    .reset(CreateInvoiceGate.close);

$showGroupLinesByGtinModal.reset(CreateInvoiceGate.close);
$forceAllowToSend.reset(CreateInvoiceGate.close);
$showGroupLinesByGtinModal.watch((data) => {
    !data && setForceAllowToSend(false);
});
